/**
 * The payment terms, shown VISUALLY (plan D10): how the money moves
 * (client → Twasta holds it → builder) and a bar where each stage is as wide
 * as its share, with a card per stage saying when it is paid in and out.
 *
 * One component for every surface that shows the terms: the Instance Admin
 * terms preview, Get it built, the request wizard, estimates, the quote, the
 * order, the workroom and the payout advice. Amounts come from the server
 * (`rows`) whenever they are available, so this never disagrees with what is
 * charged; without rows it splits `amountMinor` the same way the server does
 * (each share rounded, the last stage takes the remainder).
 *
 * Doc: docs/marketplace_get_it_built.md
 */
import { money } from '@/api/marketplace'

export interface StageLike { key?: string; name: string; pct: number }
export interface StageRow extends StageLike { amount_minor: number }

interface Props {
  stages: StageLike[]
  /** Server-computed amounts per stage (preferred). */
  rows?: StageRow[]
  amountMinor?: number
  currency: string
  /** Whose point of view the labels take. */
  who?: 'client' | 'builder' | 'admin'
  /** Progress: stages fully paid to the builder. Omit for the plan view. */
  done?: number
  /** With `done`: the next stage is waiting for the client's approval. */
  review?: boolean
  advanceRelease?: 'first_approval' | 'builder_accepts'
  compact?: boolean
}

type State = 'plan' | 'done' | 'review' | 'next' | 'todo'

function split(stages: StageLike[], amount: number): number[] {
  let running = 0
  return stages.map((s, i) => {
    const a = i === stages.length - 1 ? amount - running : Math.round((amount * s.pct) / 100)
    running += a
    return a
  })
}

/** Accent → success ramp from the theme tokens, so it follows light/dark. */
function rampColor(i: number, n: number): string {
  const t = n <= 1 ? 0 : Math.round((i / (n - 1)) * 100)
  return `color-mix(in srgb, var(--color-accent), var(--color-success) ${t}%)`
}

const LOOK: Record<Exclude<State, 'plan'>, { bg: string; fg: string; border: string }> = {
  done: { bg: 'var(--color-success)', fg: '#fff', border: 'none' },
  review: { bg: 'var(--color-warning)', fg: '#fff', border: 'none' },
  next: { bg: 'var(--color-surface-2)', fg: 'var(--color-accent)', border: '2px dashed var(--color-accent)' },
  todo: { bg: 'var(--color-surface-2)', fg: 'var(--color-text-secondary)', border: 'none' },
}

export default function PaymentStages({
  stages, rows, amountMinor = 0, currency, who = 'client', done, review = false,
  advanceRelease = 'first_approval', compact = false,
}: Props) {
  if (!stages?.length) return null
  const amounts = rows && rows.length === stages.length ? rows.map((r) => r.amount_minor) : split(stages, amountMinor)
  const last = stages.length - 1
  const progress = typeof done === 'number' && done >= 0
  const payee = who === 'builder' ? 'you' : 'the builder'
  const fromLabel = who === 'client' ? 'You' : 'Client'
  const toLabel = who === 'builder' ? 'You' : 'Builder'

  const stateOf = (i: number): State => {
    if (!progress) return 'plan'
    if (i < (done as number)) return 'done'
    if (i === done) return review ? 'review' : 'next'
    return 'todo'
  }
  const statusText: Record<State, string> = {
    plan: '', done: who === 'builder' ? 'Paid to you' : 'Paid to the builder',
    review: 'Waiting for approval', next: 'Next', todo: 'Not started',
  }

  const chip = (label: string, initial: string, accent = false) => (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 8, borderRadius: 999, padding: '6px 12px', fontWeight: 600,
      background: accent ? 'var(--color-accent-weak)' : 'var(--color-surface-2)',
      border: `1px solid ${accent ? 'var(--color-accent)' : 'var(--color-border)'}`,
      color: accent ? 'var(--color-accent)' : 'var(--color-text)',
    }}>
      {accent ? (
        <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="11" width="16" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></svg>
      ) : (
        <span aria-hidden="true" style={{ width: 22, height: 22, borderRadius: 999, background: 'var(--color-accent)', color: '#fff', fontSize: 12, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>{initial}</span>
      )}
      {label}
    </span>
  )
  const arrow = (label: string) => (
    <span style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', color: 'var(--color-text-secondary)', fontSize: 13, minWidth: 110 }}>
      <span>{label}</span>
      <svg aria-hidden="true" width="110" height="10" viewBox="0 0 110 10"><path d="M0 5h102" stroke="currentColor" strokeWidth="2" /><path d="M100 1l6 4-6 4" fill="none" stroke="currentColor" strokeWidth="2" /></svg>
    </span>
  )

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, fontSize: 14 }}>
      {!compact && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }} aria-label="How the money moves">
          {chip(fromLabel, fromLabel[0])}
          {arrow('pays each share')}
          {chip('Twasta holds it', '', true)}
          {arrow('pays on approval')}
          {chip(toLabel, toLabel[0])}
        </div>
      )}

      <div role="img" aria-label={`Payment stages: ${stages.map((s) => `${s.name} ${s.pct}%`).join(', ')}`}
        style={{ display: 'flex', gap: 3, height: 38, width: '100%' }}>
        {stages.map((s, i) => {
          const st = stateOf(i)
          const look = st === 'plan' ? { bg: rampColor(i, stages.length), fg: '#fff', border: 'none' } : LOOK[st]
          return (
            <div key={s.key || i} style={{
              flex: `${Math.max(s.pct, 1)} 1 0`, minWidth: 34, background: look.bg, color: look.fg, border: look.border,
              borderRadius: i === 0 ? '10px 4px 4px 10px' : i === last ? '4px 10px 10px 4px' : 4,
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 13, boxSizing: 'border-box',
            }}>{s.pct}%</div>
          )
        })}
      </div>

      {!compact && (
        <ol style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 8 }}>
          {stages.map((s, i) => {
            const st = stateOf(i)
            const color = st === 'plan' ? rampColor(i, stages.length) : LOOK[st].bg === 'var(--color-surface-2)' ? 'var(--color-border-strong)' : LOOK[st].bg
            const out = i === 0
              ? (advanceRelease === 'builder_accepts' ? `to ${payee} when the order is accepted` : `to ${payee} with the first approval`)
              : i === last ? `to ${payee} at sign-off` : `to ${payee} on approval`
            return (
              <li key={s.key || i} style={{ border: '1px solid var(--color-border)', borderTop: `4px solid ${color}`, borderRadius: 10, padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 3, background: 'var(--color-bg-secondary)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 6, alignItems: 'baseline' }}>
                  <strong style={{ fontSize: 14 }}>{s.name}</strong>
                  <span style={{ fontWeight: 700, fontSize: 13, color: 'var(--color-text-secondary)' }}>{s.pct}%</span>
                </div>
                <span style={{ fontFamily: 'var(--font-mono, monospace)', fontWeight: 600, fontSize: 15 }}>{money(amounts[i], currency)}</span>
                <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>In: {i === 0 ? 'paid on order' : 'paid when submitted'}</span>
                <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>Out: {out}</span>
                {progress && (
                  <span style={{ alignSelf: 'flex-start', marginTop: 4, fontSize: 13, fontWeight: 600, padding: '2px 9px', borderRadius: 999,
                    background: st === 'done' ? 'var(--color-success-weak)' : st === 'review' ? 'var(--color-warning-weak)' : st === 'next' ? 'var(--color-accent-weak)' : 'var(--color-surface-2)',
                    color: st === 'done' ? 'var(--color-success)' : st === 'review' ? 'var(--color-warning)' : st === 'next' ? 'var(--color-accent)' : 'var(--color-text-secondary)' }}>
                    {statusText[st]}
                  </span>
                )}
              </li>
            )
          })}
        </ol>
      )}
    </div>
  )
}
